import {
  useUserStudioAiSubscriptionAccessQuery,
  useUserSubscriptionVariantQuery,
} from '@repo/cf-user/hooks/use-user-data'
import { isStudioDesktop } from '@repo/studio-desktop-bridge/runtime'
import { ROUTES } from '@repo/studio-utils/routes'
import * as React from 'react'
import { useMatchMedia } from 'hooks/use-match-media'
import { getCookie, setCookie } from 'utils/dom'
import { stripStudioLocalePrefix } from '@repo/react-router-locale/canonicalize-studio-locale-pathname'
import { isStudioAiPricingPlan } from 'payments/studio-ai-plans'

const SMALL_VIEWPORT_QUERY = '(max-width: 767px)'
const DISMISSED_VALUE = 'dismissed'

function getTodayStamp() {
  return new Date().toDateString()
}

interface EligibilityOptions {
  cookieName: string
  intervalMs: number
  hideOnSmallViewport?: boolean
  /**
   * Caller-specific requirement AND-ed into eligibility (e.g. the switch modal requires the
   * subscriber to not already be on the target plan). Defaults to `true` so callers that only need
   * the active-subscriber + cadence rules are unaffected.
   */
  meetsAdditionalRequirement?: boolean
}

/**
 * Side-effect-free eligibility signals — a coordinator can compare several promos and pick one
 * without any of them recording a view. Only `usePromoEligibility` (the mounted promo) stamps.
 */
export function usePromoEligibilityRead({
  cookieName,
  hideOnSmallViewport = false,
  meetsAdditionalRequirement = true,
}: Omit<EligibilityOptions, 'intervalMs'>) {
  const { data: subscriptionVariant, isPending } = useUserSubscriptionVariantQuery('studioAi')
  const { data: studioAccess } = useUserStudioAiSubscriptionAccessQuery()
  const currentPlan = studioAccess?.subscription?.type
  const supportsOffer = currentPlan !== undefined && !isStudioAiPricingPlan(currentPlan)
  const isSmallViewport = useMatchMedia(SMALL_VIEWPORT_QUERY)
  const isHiddenOnSmallViewport = hideOnSmallViewport && isSmallViewport

  const lastShownStamp = getCookie(cookieName)
  const isWithinShowWindow = !lastShownStamp || lastShownStamp === getTodayStamp()
  const isActiveSubscriber = subscriptionVariant === 'subscribed'
  // Promo modals/banner mount in the app shell, which Studiobook surfaces reuse.
  // Suppress them there so browsing the gallery never triggers a live promo. Guard the
  // `window` read so the shared package stays SSR-safe (e.g. Freemode's prerendered landing pages).
  const pathname =
    typeof window === 'undefined' ? '' : stripStudioLocalePrefix(window.location.pathname)
  const isPreviewSurface = [
    ROUTES.DESIGN,
    ROUTES.FEATURES,
    ROUTES.ROUTE_STORIES,
    ROUTES.COMPONENTS,
  ].some(prefix => pathname.startsWith(prefix))
  const qualifiesToShow =
    isActiveSubscriber &&
    supportsOffer &&
    meetsAdditionalRequirement &&
    isWithinShowWindow &&
    !isPreviewSurface &&
    !isStudioDesktop()

  return { isPending, qualifiesToShow, isHiddenOnSmallViewport, isPreviewSurface, supportsOffer }
}

/**
 * Eligibility for a single mounted modal/banner gate: latches once shown (so a query refetch can't
 * flip it closed) and stamps the "shown today" cookie.
 */
export function usePromoEligibility(options: EligibilityOptions) {
  const { cookieName, intervalMs } = options
  const { qualifiesToShow, isHiddenOnSmallViewport, isPreviewSurface, supportsOffer } =
    usePromoEligibilityRead(options)

  const [hasEverQualified, setHasEverQualified] = React.useState(false)
  if (qualifiesToShow && !hasEverQualified) {
    setHasEverQualified(true)
  }

  React.useEffect(() => {
    const shouldStampToday =
      qualifiesToShow && !isHiddenOnSmallViewport && getCookie(cookieName) !== getTodayStamp()
    if (shouldStampToday) {
      setCookie({ name: cookieName, value: getTodayStamp(), expiresIn: intervalMs })
    }
  }, [qualifiesToShow, isHiddenOnSmallViewport, cookieName, intervalMs])

  const dismiss = React.useCallback(() => {
    setCookie({ name: cookieName, value: DISMISSED_VALUE, expiresIn: intervalMs })
  }, [cookieName, intervalMs])

  return {
    isEligible: hasEverQualified && supportsOffer && !isHiddenOnSmallViewport && !isPreviewSurface,
    dismiss,
  }
}
